import HelpLayout from "@/components/layout/HelpLayout";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import ChamadoFileUpload from "@/components/ui/chamado-file-upload";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import HelpNavigationTabs from "@/components/ui/help-navigation-tabs";
import PendenciaCard from "@/components/ui/pendencia-card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import {
  fetchSacChamados,
  fetchSacCotacoes,
  fetchSacMotivos,
  gravarChamado,
} from "@/services/chamados";
import { SacMotivo, SacMotivoGrupo } from "@/types/Chamados";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, ChevronDownIcon, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

type ContractOption = { value: string; label: string };

const chamadoSchema = z.object({
  contract: z.string().min(1, "Selecione um contrato"),
  dataOcorrido: z.date({
    message: "Selecione a data do ocorrido",
  }),
  grupo: z.string().min(1, "Selecione o grupo do motivo"),
  motivo: z.string().min(1, "Selecione o motivo do chamado"),
  descricao: z
    .string()
    .min(10, "Descreva seu problema com pelo menos 10 caracteres"),
});

type ChamadoFormValues = z.infer<typeof chamadoSchema>;

export default function AbrirChamadoPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [motivosGrupos, setMotivosGrupos] = useState<SacMotivoGrupo[]>([]);
  const [motivosOptions, setMotivosOptions] = useState<SacMotivo[]>([]);
  const [selectedPlaceholder, setSelectedPlaceholder] = useState(
    "Descreva detalhadamente seu problema",
  );
  const [contracts, setContracts] = useState<ContractOption[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [hasOpenChamados, setHasOpenChamados] = useState(false);
  const [openChamadosCount, setOpenChamadosCount] = useState(0);

  const form = useForm<ChamadoFormValues>({
    resolver: zodResolver(chamadoSchema),
    defaultValues: {
      contract: "",
      dataOcorrido: new Date(),
      grupo: "",
      motivo: "",
      descricao: "",
    },
  });

  useEffect(() => {
    async function loadMotivos() {
      try {
        const data = await fetchSacMotivos();
        setMotivosGrupos(data);
      } catch (err) {
        console.error("Erro ao buscar motivos:", err);
      }
    }

    async function loadContracts() {
      try {
        const cotacoes = await fetchSacCotacoes(user?.cli_codigo || "");

        const opts = cotacoes.map((c) => ({
          value: c.cve_codigo || "",
          label: `#${c.ctc_codigo || "N/A"} • ${
            c.veiculo || "Veículo não informado"
          } • ${c.origem || "Origem"} → ${c.destino || "Destino"}`,
        }));
        setContracts(opts);
      } catch (err) {
        console.error("Erro ao carregar cotações:", err);
        setContracts([]);
      }
    }

    async function loadOpenChamados() {
      try {
        const chamados = await fetchSacChamados(user?.cli_codigo || "");
        const open = chamados.filter(
          (c) => c.sst_codigo !== "3" && c.sst_codigo !== "4",
        );
        setHasOpenChamados(open.length > 0);
        setOpenChamadosCount(open.length);
      } catch (err) {
        console.error("Erro ao carregar chamados do cliente:", err);
      }
    }

    loadMotivos();
    loadContracts();
    loadOpenChamados();
  }, [user?.cli_codigo]);

  // Previne o usuário de sair da página durante o upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isLoading) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isLoading]);

  const onSubmit = async (data: ChamadoFormValues) => {
    setIsLoading(true);

    try {
      const response = await gravarChamado(
        data.dataOcorrido.toISOString().split("T")[0],
        data.descricao,
        user?.cli_codigo || "",
        data.contract,
        data.motivo,
        attachedFiles,
      );

      if (response.sucesso) {
        toast({
          title: "Chamado aberto com sucesso!",
          description: `Seu chamado #${response.sac_codigo} foi registrado e em breve nossa equipe entrará em contato.`,
          variant: "default",
        });

        form.reset();
        setSelectedPlaceholder("Descreva detalhadamente seu problema");
        setMotivosOptions([]);
        setAttachedFiles([]);

        setTimeout(() => {
          navigate("/cliente/ajuda/chamados-abertos");
        }, 2000);
      } else {
        throw new Error(response.mensagem || "Erro ao processar chamado");
      }
    } catch (err) {
      toast({
        title: "Erro ao abrir chamado",
        description:
          err instanceof Error
            ? err.message
            : "Ocorreu um erro ao processar sua solicitação. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <HelpLayout title="Abrir novo chamado">
      <div className="flex flex-col gap-6 overflow-hidden">
        <div className="flex flex-col w-full space-y-6 ">
          <PendenciaCard mode="screen" screenName="FAQ / Suporte" />

          <div className="rounded-2xl border sm:space-y-6 border-gray-200 bg-white p-3 shadow-sm sm:p-6">
            <HelpNavigationTabs className="mb-6" />
            <h2 className="text-lg font-semibold text-n-green sm:text-xl">
              Deseja abrir um chamado?
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Se você possui alguma dúvida em relação ao seu transporte,
              agendamento ou status, fale conosco.
            </p>

            {hasOpenChamados ? (
              <div className="rounded-2xl  border border-n-green-2 bg-gradient-to-r from-n-green-s-light to-n-green-0 p-6 shadow-sm md:flex md:items-center md:gap-6">
                <div className="flex-shrink-0 flex items-center justify-center w-20 h-20 rounded-lg bg-white/60 border border-white/30 shadow-inner">
                  <AlertTriangle className="h-10 w-10 text-n-green-7" />
                </div>

                <div className="mt-3 md:mt-0 md:flex-1">
                  <h3 className="text-lg font-semibold text-n-green-9">
                    Você possui chamados em aberto
                  </h3>
                  <p className="mt-2 text-sm text-n-green-6">
                    Para abrir um novo chamado, por favor finalize os que estão
                    em aberto. ({openChamadosCount} em aberto)
                  </p>
                </div>

                <div className="mt-4 md:mt-0 md:ml-4 flex-shrink-0">
                  <Button
                    variant="green"
                    onClick={() => navigate("/cliente/ajuda/chamados-abertos")}
                    className="px-5 py-2"
                  >
                    Ver chamados em aberto
                  </Button>
                </div>
              </div>
            ) : (
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  className="flex flex-col gap-4"
                >
                  <FormField
                    control={form.control}
                    name="contract"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                          }}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <SelectValue placeholder="Selecione o contrato" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {contracts.map((contract) => (
                              <SelectItem
                                key={contract.value}
                                value={contract.value}
                              >
                                <span className="block truncate sm:whitespace-normal max-w-[280px] sm:max-w-none">
                                  {contract.label}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="dataOcorrido"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="border"
                                id="dataOcorrido"
                                className={cn(
                                  "w-full justify-between font-normal h-12 text-n-primary hover:text-n-primary rounded-xl border-2 bg- border-gray-300",
                                  !field.value && "text-muted-foreground",
                                )}
                              >
                                {field.value
                                  ? field.value.toLocaleDateString("pt-BR")
                                  : "Selecione a data"}
                                <ChevronDownIcon className="h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-auto overflow-hidden p-0"
                            align="start"
                          >
                            <Calendar
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              captionLayout="dropdown"
                              disabled={(date) =>
                                date > new Date() ||
                                date < new Date("1900-01-01")
                              }
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="grupo"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            const selectedGrupo = motivosGrupos.find(
                              (g) => g.descricao === value,
                            );
                            if (selectedGrupo) {
                              setMotivosOptions(selectedGrupo.motivos);
                            }
                            form.setValue("motivo", "");
                          }}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <SelectValue placeholder="Selecione o grupo do motivo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {motivosGrupos.map((grupo) => (
                              <SelectItem
                                key={grupo.descricao}
                                value={grupo.descricao}
                              >
                                {grupo.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="motivo"
                    render={({ field }) => (
                      <FormItem>
                        <Select
                          value={field.value}
                          onValueChange={(value) => {
                            field.onChange(value);
                            const selected = motivosOptions.find(
                              (m) => m.stc_codigo.toString() === value,
                            );
                            if (selected) {
                              setSelectedPlaceholder(selected.stc_placeholder);
                            }
                          }}
                          disabled={motivosOptions.length === 0}
                        >
                          <FormControl>
                            <SelectTrigger
                              className={cn(
                                "w-full",
                                !field.value && "text-muted-foreground",
                              )}
                            >
                              <SelectValue placeholder="Selecione o motivo do chamado" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {motivosOptions.map((motivo) => (
                              <SelectItem
                                key={motivo.stc_codigo}
                                value={motivo.stc_codigo.toString()}
                              >
                                {motivo.stc_motivo}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="descricao"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder={selectedPlaceholder}
                            rows={6}
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="mt-4">
                    <ChamadoFileUpload
                      onFilesChange={setAttachedFiles}
                      maxFiles={10}
                    />
                  </div>

                  <div className="flex justify-end mt-6">
                    <Button
                      type="submit"
                      variant="green"
                      size="lg"
                      className="w-full sm:w-auto"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          Abrindo chamado...
                          <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                        </>
                      ) : (
                        "Abrir chamado"
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        </div>

        <Dialog open={isLoading}>
          <DialogContent className="sm:max-w-md [&>button]:hidden">
            <div className="flex flex-col items-center justify-center py-8 px-4 space-y-6">
              <div className="relative">
                <div className="w-20 h-20 border-4 border-n-green-2 border-t-n-green rounded-full animate-spin" />
              </div>

              <div className="text-center space-y-3">
                <h3 className="text-xl font-semibold text-n-green-9">
                  Enviando seu chamado...
                </h3>
                <p className="text-sm text-n-green-6 max-w-sm">
                  Estamos processando sua solicitação. Este processo pode levar
                  alguns instantes.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3 w-full">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900">
                    Por favor, não saia desta página
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Aguarde até que o processo seja concluído para evitar perda
                    de dados.
                  </p>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* <div className="w-full mt-6 lg:mt-0 lg:basis-1/3">
          <NewQuoteCard
            onClick={() => window.open("/fazer-cotacao", "_blank")}
          />
        </div> */}
      </div>
    </HelpLayout>
  );
}
